/**
 * Thin, typed wrapper around Plausible's `window.plausible` queue.
 *
 * - Safe to call before the Plausible script has loaded (events queue up).
 * - Safe to call when the script never loads (ad-blocker, dev, SSR) — silently no-ops.
 * - Strips falsy/empty props so the dashboard breakdown stays clean.
 */

type PlausibleEventOptions = {
  props?: Record<string, string | number | boolean | undefined | null>;
  callback?: () => void;
};

declare global {
  interface Window {
    plausible?: ((eventName: string, options?: PlausibleEventOptions) => void) & {
      q?: unknown[];
    };
  }
}

export type LeadEvent =
  | 'CTA Click'
  | 'Waitlist Started'
  | 'Waitlist Submitted'
  | 'Waitlist Error'
  | 'Contact Started'
  | 'Contact Submitted'
  | 'Contact Error'
  | 'Heat Seeder Used';

/**
 * Where on the site a lead-intent action originated. Keep this list short and
 * stable so the Plausible "location" breakdown is meaningful over time.
 */
export type CtaLocation =
  | 'hero'
  | 'nav'
  | 'nav_mobile'
  | 'footer'
  | 'blog_index'
  | 'blog_post'
  | 'compare'
  | 'heat_seeder'
  | 'tools_index'
  | 'home_inline'
  | 'waitlist_form';

export type CtaKind = 'Waitlist' | 'Contact' | 'Compare' | 'Heat Seeder';

function cleanProps(
  props?: PlausibleEventOptions['props'],
): Record<string, string | number | boolean> | undefined {
  if (!props) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function track(event: LeadEvent, props?: PlausibleEventOptions['props']): void {
  if (typeof window === 'undefined') return;
  try {
    const fn = window.plausible;
    if (typeof fn !== 'function') return;
    const cleaned = cleanProps(props);
    if (cleaned) {
      fn(event, { props: cleaned });
    } else {
      fn(event);
    }
  } catch {
    // analytics must never break the app
  }
}

export function trackCta(cta: CtaKind, location: CtaLocation): void {
  track('CTA Click', { cta, location });
}
